/// Estimated Position (EP) — the DR-plus-tide vector construction.
///
/// From a known start (a fix), plot the water track — the course
/// steered through the water, run for the elapsed time at boat speed —
/// to reach the DR position. From the DR, lay off the tidal stream
/// (set + drift over the same time) to reach the EP. The line from the
/// start to the EP is the course (and distance) made good over the
/// ground.
///
/// RYA arrow convention: water track = one arrowhead, ground track
/// (course made good) = two, tidal vector = three.
///
/// Leeway (wind pushing the boat to leeward of her heading) is a
/// further refinement not modelled here — these problems assume none.
///
/// Directions are degrees True; distances in nautical miles, speeds in
/// knots. Pure logic, fully unit-tested.

const DEG = Math.PI / 180;

function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/// Signed angle a−b normalised to (−180, 180].
function signedDelta(a: number, b: number): number {
  const d = normalizeDeg(a - b);
  return d > 180 ? d - 360 : d;
}

export interface EpInput {
  /// Course steered through the water (the heading), °T.
  courseSteeredDeg: number;
  /// Boat speed through the water, knots.
  boatSpeedKts: number;
  /// Time elapsed, hours.
  hours: number;
  /// Tidal stream direction — the way it flows TOWARD, °T.
  setDeg: number;
  /// Tidal stream rate, knots.
  rateKts: number;
  /// Leeway angle (magnitude, degrees). Wind pushes the boat to
  /// leeward of her heading, so the actual water track is offset by
  /// this much downwind. Omit (or 0) for no leeway.
  leewayDeg?: number;
  /// Direction the wind blows FROM, °T. Needed to know which side the
  /// leeway offsets to. Ignored when there's no leeway.
  windFromDeg?: number;
}

export interface EpResult {
  /// Distance run through the water (boat speed × time), nm.
  distanceRunNm: number;
  /// Tidal drift over the period (rate × time), nm.
  tideDriftNm: number;
  /// Bearing from the start to the EP — the course made good, °T.
  courseMadeGoodDeg: number;
  /// Straight-line distance from the start to the EP, nm.
  distanceMadeGoodNm: number;
  /// Speed made good over the ground, knots.
  speedOverGroundKts: number;
  /// EP position relative to the start, in nautical miles
  /// (east + / west −, north + / south −). Useful for plotting.
  eastingNm: number;
  northingNm: number;
  /// Course the boat actually makes through the water — the heading
  /// offset downwind by the leeway angle. Equals the course steered
  /// when there's no leeway.
  waterTrackDeg: number;
}

/// The course made good through the water — the heading offset to
/// leeward by the leeway angle. Wind from the starboard side pushes the
/// track to port, and vice versa.
function waterTrack(input: EpInput): number {
  const leeway = input.leewayDeg ?? 0;
  if (leeway <= 0 || input.windFromDeg === undefined) {
    return normalizeDeg(input.courseSteeredDeg);
  }
  const rel = signedDelta(input.windFromDeg, input.courseSteeredDeg);
  // rel > 0: wind from starboard → boat set to port (subtract).
  // rel < 0: wind from port → boat set to starboard (add).
  return normalizeDeg(input.courseSteeredDeg - Math.sign(rel) * leeway);
}

export function estimatedPosition(input: EpInput): EpResult {
  const distanceRunNm = input.boatSpeedKts * input.hours;
  const tideDriftNm = input.rateKts * input.hours;

  const waterTrackDeg = waterTrack(input);
  const wx = distanceRunNm * Math.sin(waterTrackDeg * DEG);
  const wy = distanceRunNm * Math.cos(waterTrackDeg * DEG);
  const tx = tideDriftNm * Math.sin(input.setDeg * DEG);
  const ty = tideDriftNm * Math.cos(input.setDeg * DEG);

  const eastingNm = wx + tx;
  const northingNm = wy + ty;

  const distanceMadeGoodNm = Math.hypot(eastingNm, northingNm);
  const courseMadeGoodDeg =
    distanceMadeGoodNm < 1e-9
      ? 0
      : normalizeDeg(Math.atan2(eastingNm, northingNm) / DEG);
  const speedOverGroundKts =
    input.hours > 0 ? distanceMadeGoodNm / input.hours : 0;

  return {
    distanceRunNm,
    tideDriftNm,
    courseMadeGoodDeg,
    distanceMadeGoodNm,
    speedOverGroundKts,
    eastingNm,
    northingNm,
    waterTrackDeg,
  };
}
